/**
 * Die Formate der Bot-Steuerung (`v2Prompt.md` §4, §5, §25, §26, §36; Zyklus `V2-2a`).
 *
 * **Warum das hier liegt und nicht in einem Bot-Paket:** diese Typen **reisen**. `PlayerControl`
 * geht über REST, wenn ein Host einen Bot auf einen Platz setzt, und `BotDefinition` steckt im
 * Datenmodell (`game_players.bot`) und im Remote-Protokoll (§27). `packages/contracts` ist das
 * Blatt, von dem alle abhängen — lägen sie in `packages/bots`, müsste dieses Paket davon
 * abhängen und die Abhängigkeitsrichtung kehrte sich um (`docs/BOT_ARCHITECTURE.md` 7.2).
 *
 * **Was hier bewusst *nicht* liegt: das Verhalten.** `BotStrategy`, `DecisionContext`, Registry,
 * Evaluation und Suche sind `packages/bots` (ab `V2-2c`). Dieses Modul beschreibt die Form, nicht
 * die Entscheidung — wie jedes Schema dieses Pakets.
 *
 * **Die beiden Sätze unten nennen nur, was dieser Dienst wirklich ausführen kann**, und wachsen
 * je Phase. Ein offener String nähme die OpenAPI-Dokumentation und die Tippfehlerprüfung; ein
 * geschlossener Satz mit noch nicht gebauten Einträgen wäre schlimmer als beides — der Dienst
 * nähme einen Bot an, den er nicht ausführen kann, und es fiele im **ersten Zug einer lebenden
 * Partie** auf. `V2-2c` hält zusätzlich fest, dass `STRATEGY_IDS` genau die registrierten
 * Strategien sind.
 */
import { z } from 'zod';
/**
 * Die Strategien, die ausgeführt werden können (§4).
 *
 * Die **puren** Strategien — die, die `packages/bots` registriert. `V2-2a` legt den Satz an,
 * `V2-2c` liefert `random`, `V2-4b` `greedy`, `V2-5c` `alphaBeta`, `V2-7b` `maxN` (`mcts` ist mit
 * **E82** vorbereitet, nicht gebaut). Jeder Zugang ist **eine Zeile hier plus ein Eintrag in der
 * Registry** — und ein Test, der beide gegeneinander hält. `llm` steht **nicht** hier, sondern
 * in `STRATEGY_IDS` darunter.
 *
 * **`human` wird hier nie stehen.** Ein Mensch schiebt, ein Bot wird gezogen; einen
 * `HumanController` gibt es in diesem Projekt bewusst nicht (`BOT_ARCHITECTURE.md` 11.1). Die
 * Kennung ist deshalb der Platzhalter der Tests für „eine Strategie, die es nie geben wird" —
 * anders als `mcts`, das in `V2-7` kommt (die Lehre aus 11f).
 *
 * **`alphaBeta` deckt §17 *und* §18 ab, und das ist eine Entscheidung** (**E75**): ohne Pruning
 * *ist* Alpha-Beta Minimax, zwei Kennungen wären zwei Zugänge zu einer Rechnung. Wer Minimax
 * will, setzt `pruning: false` in der Konfiguration.
 */
export const PURE_STRATEGY_IDS = ['random', 'greedy', 'alphaBeta', 'maxN'];
/**
 * **Alle** Strategien: die puren und `llm` (`V2-9a`).
 *
 * **Zwei Sätze, weil es zwei Registrys gibt.** `packages/bots` ist pur und ohne externe
 * Laufzeit-Dependencies (**E56**) und kann eine Strategie, die einen Provider über HTTP fragt,
 * nicht registrieren — sie hält `PURE_STRATEGY_IDS` in beide Richtungen gegen ihre Registry.
 * `llm` löst der **Dienst** auf, über `packages/ai` und die Provider, die er eingerichtet hat
 * (`apps/server/src/bots/strategies.ts`). Ein einziger Satz ließe sich gegen keine der beiden
 * Registrys vollständig prüfen.
 *
 * **`llm` ist eine Strategie, kein Spielertyp** (§31): ein LLM-Bot ist ein `BOT` wie jeder
 * andere, zieht über denselben Treiber und dieselben Sitzungsmethoden.
 */
export const STRATEGY_IDS = [...PURE_STRATEGY_IDS, 'llm'];
export const strategyIdSchema = z.enum(STRATEGY_IDS);
/** Braucht diese Strategie nichts als die Engine? Dann kennt sie `packages/bots`. */
export function isPureStrategyId(id) {
    return PURE_STRATEGY_IDS.includes(id);
}
/**
 * Die Stärkestufen der Lobby (**E104**, `L1`) — englisch und stabil (Regel 6); „leicht · mittel
 * · stark" kommen aus den Übersetzungen. **Was** sie bedeuten, weiß nur der Dienst
 * (`apps/server/src/bots/levels.ts`, **E106**): eine Stufe ist ein Versprechen über den Gegner in
 * einer lebenden Partie, und das hängt an Ausführungsart und Obergrenzen des Dienstes.
 */
export const BOT_LEVELS = ['easy', 'medium', 'strong'];
/**
 * Wie tief ein Host eine Suche höchstens einstellen kann (**E104** Teilfrage 2) — **gemessen,
 * nicht gesetzt** (`L1a`, **E105**): Tiefe 3 ohne `mobility` kostet im Mittel 56 ms, Tiefe 4 bis
 * 10,5 s auf dem Mac. Dieselbe Zahl ist die Obergrenze eines Threads im Dienst
 * (`WORKER_MAX_DEPTH`) — sie liest sie von hier, damit es eine Quelle gibt.
 */
export const BOT_MAX_DEPTH = 3;
/**
 * Wie viele Gewichte eine Wahl höchstens nennt. Es gibt sieben (`packages/bots`); ob ein Name
 * existiert, prüft die Strategie beim Einsetzen (**E71**). Die Grenze hier hält nur die Anfrage
 * klein — ohne sie wäre `weights` ein beliebig großes Objekt, das bis zur Strategie reist.
 */
export const BOT_WEIGHTS_MAX = 16;
/**
 * Welche Stufen **erst beim Start** zu einer Strategie werden (`L1c1`) — nur `strong`: es heißt
 * im Duell `alphaBeta` und ab drei Seiten `maxN`, und die Zahl der Seiten steht erst beim Start
 * fest. **Hier und nicht im Dienst**, weil zwei Leser sie brauchen, die nur Pakete auflösen
 * können: die `CHECK`-Bedingung im Datenbankschema (gelesen auch von `drizzle-kit`) und die
 * Lobby-Ansicht, die eine offene Stufe als solche zeigt.
 */
export const LEVELS_RESOLVED_AT_START = ['strong'];
/**
 * Wie lang ein Name höchstens ist (`V2-9e2b`) — Provider, Modell, Skill. Ein Name steht in
 * **jeder** Anfrage und in jeder Zeile von `llm_calls`; ohne Grenze wäre eine Zeile Konfiguration
 * ein beliebig großer Eintrag je Aufruf.
 */
export const LLM_NAME_MAX = 128;
/** Ein Name, der nicht nur aus Leerraum besteht — und kurz ist. */
const nameSchema = z
    .string()
    .max(LLM_NAME_MAX)
    .refine((value) => value.trim().length > 0, { message: 'darf nicht leer sein' });
/**
 * Wie viele Skills eine Konfiguration höchstens nennt (`V2-9e2b`, Befund der Sicherheitsprüfung).
 * Jeder Name trägt den **ganzen** Text des Skills in jede Anfrage — eine unbegrenzte Liste wäre ein
 * beliebig großer Prompt, den der Wächter ablehnte: der Bot fragte dann **still** nie ein Modell.
 * Großzügig gegenüber den sieben Dateien von `V2-9e2`.
 */
export const LLM_SKILLS_MAX = 16;
/** Der erste Name, der in der Liste schon einmal stand — oder `undefined`. */
function ersterDoppelter(namen) {
    return namen.find((name, index) => namen.indexOf(name) !== index);
}
/**
 * Die Konfiguration der Strategie `llm` — **welches Modell bei welchem Provider** (§116).
 *
 * Sie steht in `BotDefinition.config` und damit in `game_players.bot`, also **in der
 * Datenbank**. Daraus folgen zwei Dinge:
 *
 * - **`strictObject`, und das ist eine Sicherheitszusage** (§121): ein Schlüssel gehört nach
 *   `.env` (Entscheidung des Users, 2026-10-11) — ein Feld `apiKey` würde hier abgewiesen, statt
 *   still in einer Tabellenzeile zu landen.
 * - **Das Modell ist eine Zeichenkette, keine feste Liste** (§120): ob es ein Modell gibt, weiß
 *   der Provider. Ob es den **Provider** gibt, prüft der Dienst beim Einsetzen des Bots — gegen
 *   die Provider, die er eingerichtet hat, nicht gegen eine Liste hier.
 *
 * **Frist und Versuche seit `V2-9c`** — mit dem Zyklus, der sie **liest** (die Lehre aus
 * `V2-8b`: ein Feld ohne Leser ist eine Zusage, die niemand einhält). Beide mit **Vorgabe**,
 * damit eine Definition aus `V2-9a` gültig bleibt. **Hier und nicht in `budget`**:
 * `budget.maxTimeMs` ist in Knoten belegt (**E77**) — eine Suche hat keine Uhr, ein Provider
 * schon. Skills und Strategie-Version kommen mit `V2-9e`.
 *
 * **Die Versuche sind gedeckelt** (§100): jeder kostet Geld, und ein Modell, das dreimal eine
 * erfundene ID nennt, wird es beim sechsten Mal nicht anders machen.
 */
export const LLM_DEFAULT_TIMEOUT_MS = 30_000;
export const LLM_DEFAULT_MAX_ATTEMPTS = 3;
/**
 * Die Obergrenze der **Ausgabe** je Aufruf (`V2-9d`) — zwei Leser: der Wächter rechnet vor dem
 * Aufruf mit ihr (eine Reservierung muss den schlimmsten Fall decken), und der Adapter
 * (`V2-9e`) schickt sie als `max_tokens`. Sonst stimmte die Reservierung nicht mit dem überein,
 * was ein Aufruf höchstens kosten kann.
 */
export const LLM_DEFAULT_MAX_OUTPUT_TOKENS = 1_024;
export const llmConfigSchema = z.strictObject({
    providerId: nameSchema,
    modelId: nameSchema,
    /** Die Frist **je Versuch**, in Millisekunden Wanduhrzeit — gemessen im Dienst, nicht hier. */
    timeoutMs: z.number().int().min(1).max(300_000).prefault(LLM_DEFAULT_TIMEOUT_MS),
    maxAttempts: z.number().int().min(1).max(5).prefault(LLM_DEFAULT_MAX_ATTEMPTS),
    maxOutputTokens: z.number().int().min(1).max(32_000).prefault(LLM_DEFAULT_MAX_OUTPUT_TOKENS),
    /**
     * Welche Skills (`ai/skills/`, §79) das Modell bekommt (`V2-9e2`). **Ohne Angabe wählt die
     * Strategie nach der Partie** (Regeln, Figuren, Brett, dazu 1:1, FFA oder Team, in der
     * Aufstellung `SETUP_STRATEGY`). Ob es einen Namen gibt, prüft der Dienst beim Einsetzen.
     * **Begrenzt und ohne Doppelte** (`V2-9e2b`): ein doppelter Name schickte denselben Text zweimal.
     */
    skills: z
        .array(nameSchema)
        .min(1)
        .max(LLM_SKILLS_MAX)
        .superRefine((namen, ctx) => {
        const doppelt = ersterDoppelter(namen);
        if (doppelt !== undefined) {
            ctx.addIssue({ code: 'custom', message: `der Skill '${doppelt}' steht doppelt in der Liste` });
        }
    })
        .optional(),
    /** Ein freies Etikett für die Strategie (§95) — es steht in jeder Diagnose (§128). */
    strategyVersion: z.string().min(1).max(64).optional(),
});
/**
 * Wo ein Bot rechnet (§26) — **getrennt** von der Frage, *wie* er entscheidet.
 *
 * **Nur was dieser Dienst wirklich ausführen kann** — und das ist seit `V2-8a` **geprüft statt
 * zugesagt** (**E83**): ein Test in `apps/server` geht diesen Satz durch und verlangt für
 * **jeden** Eintrag einen Runner, und der `switch` in `bots/runners.ts` hat keinen `default`.
 * Ein Modus ohne Runner macht eine Lobby startbar und den Platz **tot**.
 *
 * `LOCAL_WORKER` (`node:worker_threads`) ist mit `V2-8b` dazugekommen. **`REMOTE_WORKER` kommt
 * nicht, bis ihn jemand braucht** (**E86**, 2026-10-11): er brächte keine Spielstärke, keine
 * Entlastung des Dienstes (die hat `LOCAL_WORKER` gebracht) und keinen Nutzen für das Labor
 * (§29 — `apps/sim` ist pur und synchron und **darf** keinen Worker brauchen). §26 verlangt,
 * Remote-Ausführung nicht zu *verbauen*, nicht, sie vorzudatieren — und verbaut ist nichts: ein
 * ferner Runner wäre `fetch` statt `postMessage` an derselben Naht. Die Eintrittsbedingung steht
 * in `TODO.md`.
 */
export const EXECUTION_MODES = ['IN_PROCESS', 'LOCAL_WORKER'];
const positiveInt = z.number().int().min(1);
/**
 * Was eine Entscheidung kosten darf (§25). Keine Strategie muss jeden Wert benutzen.
 *
 * **`maxTimeMs` ist ein Schutz für die lebende Partie, kein Suchparameter.** Eine Suche, die
 * nach Wanduhrzeit abbricht, liefert auf einem schnelleren Rechner ein anderes Ergebnis — in der
 * Massensimulation begrenzen deshalb ausschließlich `maxDepth`, `maxNodes` und `maxIterations`
 * (`docs/BOT_ARCHITECTURE.md` 7.2). Sonst messen wir Hardware statt Regeln, und §12 ist
 * verloren, ohne dass es auffällt.
 *
 * **Leer ist erlaubt und der Normalfall:** dann leitet der Treiber die Grenzen aus der
 * **Restzeit** des Bots ab (§25, Regelfrage Nr. 46) — er darf sich nie selbst auf Zeit verlieren.
 */
export const decisionBudgetSchema = z.strictObject({
    maxTimeMs: positiveInt.optional(),
    maxDepth: positiveInt.optional(),
    maxNodes: positiveInt.optional(),
    maxIterations: positiveInt.optional(),
});
/**
 * Ein Bot, wie er konfiguriert wird (§4): **Strategie** und **Ausführungsart** getrennt.
 *
 * Genau deshalb gibt es keine Klassen wie `LocalMinimaxBot` und `RemoteMinimaxBot` — die
 * Kombination ist Daten, nicht Vererbung.
 *
 * **`config` ist strategiespezifisch und wird hier nicht geprüft.** Dieses Paket kennt die
 * Strategien nicht; geprüft wird gegen das Schema, das die Strategie selbst mitbringt
 * (`BotStrategy.configSchema`, ab `V2-2c`). Hier gilt nur: es muss JSON-fähig sein, denn es
 * landet in `game_players.bot` — dieselbe Begründung wie beim `payload` eines Ereignisses.
 */
export const botDefinitionSchema = z.strictObject({
    strategy: strategyIdSchema,
    execution: z.enum(EXECUTION_MODES).prefault('IN_PROCESS'),
    config: z.record(z.string(), z.unknown()).prefault({}),
    budget: decisionBudgetSchema.prefault({}),
});
/**
 * Wer einen Spielerplatz steuert (§3) — **Daten am Platz, kein Interface**.
 *
 * `v2Prompt.md` §3 schlägt einen `PlayerController` mit `HumanController` und `BotController`
 * vor. Den gibt es hier nicht, und das ist eine Entscheidung (`docs/BOT_ARCHITECTURE.md` 3.1,
 * 11.1): ein `HumanController` hätte keinen Körper, denn der „Controller" eines Menschen ist der
 * Browser plus der WebSocket-Dispatch. **Ein Mensch schiebt, ein Bot wird gezogen** — ein
 * gemeinsames Interface darüber verbiegt eine der beiden Seiten.
 *
 * Damit erfährt die Engine nicht einmal, dass es Steuerungen gibt. Sie sieht am Spieler nur
 * `playerType`; *wie* ein Bot entscheidet, steht in der Datenbank und wird vom Treiber gelesen.
 *
 * **Die beiden Fälle schließen sich aus** (`strictObject` je Zweig): ein Mensch, an dem eine
 * Bot-Definition hängt, wäre eine Steuerung mit zwei Quellen.
 */
export const playerControlSchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('HUMAN') }),
    z.strictObject({ kind: z.literal('BOT'), bot: botDefinitionSchema }),
]);
const nonNegativeInt = z.number().int().min(0);
/**
 * Freitext eines Modells (`intent`, `reasoningSummary`) ist **begrenzt** — er landet im Log
 * (`onBotDecision` in `app.ts`), und ein Modell kann beliebig lang antworten.
 */
export const LLM_TEXT_MAX = 500;
/**
 * Warum ein Versuch nicht gezählt hat (§100) — **eine Art, kein Text.**
 *
 * Kein Feld für die Meldung des Providers, und das ist die Sicherheitszusage (§121): ein
 * Provider kann den Schlüssel in einer Fehlermeldung zurückspiegeln, und diese Diagnose landet
 * im Log. Was nicht aufgenommen werden **kann**, muss nicht geschwärzt werden.
 */
export const LLM_FAILURE_KINDS = [
    'timeout',
    'providerError',
    'notOffered',
    'invalidPlacement',
];
/**
 * Warum **gar nicht mehr** gefragt wurde (`V2-9d`, **E88**) — kein Fehlversuch des Modells.
 *
 * `killSwitch` (Not-Aus), `gameCap` und `dayCap` (die Deckel je Partie und je Tag), `unpriced`
 * (für dieses Modell ist kein Preis bekannt — **ohne Preis kein Aufruf**, nicht „kostet nichts").
 */
export const LLM_STOP_REASONS = ['killSwitch', 'gameCap', 'dayCap', 'unpriced'];
/**
 * Wer bei einem LLM-Bot **wirklich** entschieden hat (§101, §128, `V2-9c`).
 *
 * **`decidedBy` ist Pflicht** (**E84**): ein LLM-Bot, der still `greedy` spielt, sähe in jeder
 * Auswertung wie ein LLM aus. `model` heißt, das Modell hat gewählt; `greedy` (Zug) und
 * `randomPlacement` (Aufstellung) heißen, es ist zurückgefallen — die Diagnose der Strategie,
 * die dann gezogen hat, steht **daneben** auf der obersten Ebene.
 */
export const llmDiagnosticsSchema = z.strictObject({
    decidedBy: z.enum(['model', 'greedy', 'randomPlacement']),
    providerId: z.string().min(1),
    /** Wie in der Konfiguration (§128) — ein Alias wie „latest" ist hier noch nicht aufgelöst. */
    requestedModelId: z.string().min(1),
    /** Was der Provider gemeldet hat; fehlt, wenn er nichts gesagt hat oder nie geantwortet hat. */
    resolvedModelId: z.string().min(1).optional(),
    /**
     * Gestellte Anfragen, gescheiterte eingeschlossen. **Null**, wenn der Wächter schon die erste
     * abgelehnt hat (`stoppedBy`).
     */
    attempts: z.number().int().min(0),
    /** Je gescheitertem Versuch seine Art, in der Reihenfolge der Versuche. */
    failures: z.array(z.enum(LLM_FAILURE_KINDS)),
    /**
     * Gesetzt, wenn der Wächter einen Versuch **abgelehnt** hat (`V2-9d`) — Deckel, Not-Aus oder
     * fehlender Preis. Ein eigenes Feld und keine Art in `failures`: ein Deckel ist kein Fehler des
     * Modells, und eine Auswertung, die beides mischte, unterschätzte es.
     */
    stoppedBy: z.enum(LLM_STOP_REASONS).optional(),
    /** Die Summe über alle Versuche. */
    latencyMs: nonNegativeInt.optional(),
    /** Die Summen über alle Versuche, **soweit der Provider sie nennt** — die Kosten (`V2-9d`). */
    inputTokens: nonNegativeInt.optional(),
    outputTokens: nonNegativeInt.optional(),
    intent: z.string().max(LLM_TEXT_MAX).optional(),
    reasoningSummary: z.string().max(LLM_TEXT_MAX).optional(),
    /** Mit welchem Wissensstand gespielt wurde (§95, §128): `NAME@Version`, in Reihenfolge. */
    skills: z.array(z.string().regex(/^[A-Z0-9_]+@\d+$/u)).optional(),
    strategyVersion: z.string().min(1).max(64).optional(),
});
/**
 * Was eine Bot-Entscheidung über sich selbst sagen darf (§36, §37).
 *
 * **Ausdrücklich nicht Teil der Spielregeln** (§36): nichts hiervon beeinflusst eine Partie. Es
 * dient Debugging, Bot-Entwicklung, Balancing und Performanceanalyse.
 *
 * **Alles ist freiwillig, und der Satz steht trotzdem schon** — obwohl `V2-2a` nichts davon
 * füllt. §37 will später „Best Action / Score / Top alternatives / Evaluation" anzeigen; die
 * Felder jetzt festzulegen kostet nichts und verhindert, dass `V2-5` eine zweite Form erfindet.
 *
 * **Eine Bewertung darf negativ sein, eine Zählung nicht:** eine verlorene Stellung bewertet
 * sich unter null, ein Knotenzähler kann es nicht.
 */
export const botDiagnosticsSchema = z.strictObject({
    /** Die Bewertung der gewählten Stellung — Einheit und Vorzeichen bestimmt die Evaluation. */
    evaluation: z.number().optional(),
    depthReached: nonNegativeInt.optional(),
    nodesVisited: nonNegativeInt.optional(),
    nodesPruned: nonNegativeInt.optional(),
    durationMs: nonNegativeInt.optional(),
    /** Die Hauptvariante als Folge von Action-IDs. */
    principalVariation: z.array(z.string().min(1)).optional(),
    /** Der Beitrag je Merkmal — „+10 goal threat, +4 ring control" (§37). */
    terms: z.record(z.string(), z.number()).optional(),
    /** Die erwogenen Alternativen mit ihrer Bewertung (§37, „Top alternatives"). */
    alternatives: z
        .array(z.strictObject({ actionId: z.string().min(1), score: z.number() }))
        .optional(),
    /** Nur bei `llm` (`V2-9c`): wer entschieden hat, und was es gekostet hat. */
    llm: llmDiagnosticsSchema.optional(),
});
/**
 * Warum ein LLM-Bot einen Zug **nicht** selbst gewählt hat (`L3b`, **E112** Antwort 2) — die
 * Deckel und der Not-Aus (`stoppedBy`) und die Arten der Fehlversuche, **eine** Liste für Dienst
 * und Oberfläche.
 */
export const BOT_FALLBACK_REASONS = [...LLM_STOP_REASONS, ...LLM_FAILURE_KINDS];
/**
 * Der Rückfall am Zug-Ereignis (`L3b`, **E112** Antwort 2): Historie und ⓘ zeigen, dass ein
 * LLM-Bot diesen Zug als `greedy` gemacht hat, und warum.
 *
 * **Nur Ziel und Grund — `strictObject`, und das ist die Sicherheitszusage** (Regel 8, E112
 * Antwort 3): das Ereignis geht an **alle** am Tisch und bleibt in der Historie. Tokens, Kosten,
 * Antworttext und Modell-Freitext stehen in der Diagnose und gehen ins Log, **nie** hierher.
 */
export const botFallbackSchema = z.strictObject({
    to: z.literal('greedy'),
    /** Fehlt, wenn kein Grund erkennbar ist (etwa ein Abbruch mitten in der Befragung). */
    reason: z.enum(BOT_FALLBACK_REASONS).optional(),
});
/**
 * Der Rückfall aus der Diagnose eines **Zuges** — `undefined`, wenn das Modell entschieden hat
 * oder es keine LLM-Diagnose gibt. Der Grund ist `stoppedBy` (der Wächter hat abgelehnt), sonst
 * der **letzte** Fehlversuch: er hat den Ausschlag gegeben.
 *
 * `randomPlacement` (die Aufstellung) gibt **keinen**: sie ist kein Zug und hat kein
 * Zug-Ereignis (E112 Antwort 2 spricht vom Zug).
 */
export function botFallbackOf(diagnostics) {
    const llm = diagnostics?.llm;
    if (llm?.decidedBy !== 'greedy')
        return undefined;
    const reason = llm.stoppedBy ?? llm.failures.at(-1);
    return reason === undefined ? { to: 'greedy' } : { to: 'greedy', reason };
}
//# sourceMappingURL=bots.js.map